/**
 * Pieces the Owners page's payouts view (OwnerPayoutsView) and its payout
 * popup (OwnerPayoutDetailDialog, COL-617) share, so a payout reads the same
 * in the table row and in the popup it opens.
 */
import type { OwnerListRow } from "@/hooks/v2/useOwnerDetail";
import type { OwnerDirectoryRow } from "@/lib/owner-directory";
import type { OwnerPayoutGroup, PayoutStatus } from "@/lib/owner-payouts";
import { formatEUR } from "@/lib/v2-format";
import { cn } from "@/lib/utils";

export type Translate = (key: string, fallback: string) => string;
export type Row = OwnerDirectoryRow<OwnerListRow>;

export interface PayoutViewItem {
  row: Row;
  group: OwnerPayoutGroup & { status: PayoutStatus };
  propertyTitle: string | null;
  /** Left over from an earlier month (COL-515), or this month's own. */
  carriedOver: boolean;
}

const STATUS_META: Record<
  PayoutStatus,
  { key: string; fallback: string; tone: "emerald" | "amber" | "slate" }
> = {
  completed: {
    key: "admin.owners.payouts.status.completed",
    fallback: "Completed",
    tone: "slate",
  },
  ready: {
    key: "admin.owners.payouts.status.ready",
    fallback: "Ready",
    tone: "emerald",
  },
  pending: {
    key: "admin.owners.payouts.status.pending",
    fallback: "Pending",
    tone: "amber",
  },
};

/** "September" for a `YYYY-MM` month — with the year when asked, or when it is
 *  not this year (a December payout still open in January). */
export function monthName(
  periodMonth: string,
  language: string | undefined,
  withYear = false
): string {
  const [year, month] = periodMonth.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    month: "long",
    ...(withYear || year !== new Date().getFullYear()
      ? { year: "numeric" }
      : {}),
  });
}

/** Real apartments in a group — the synthetic `property-level-<id>` bucket for
 *  unassigned rooms is not an apartment an admin would count. */
export function apartmentCount(group: OwnerPayoutGroup): number {
  return group.apartmentIds.filter((id) => !id.startsWith("property-level-"))
    .length;
}

export function eur(value: number): string {
  return formatEUR(value) ?? "—";
}

/** Same pill shell as OwnersTable's, so payout status reads as one family with
 *  the onboarding and invoice pills on the owner rows. */
export function StatusPill({
  status,
  t,
}: {
  status: PayoutStatus;
  t: Translate;
}) {
  const meta = STATUS_META[status];
  const tone = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    slate: "bg-muted text-muted-foreground border-transparent",
  }[meta.tone];
  const dot = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    slate: "bg-muted-foreground/40",
  }[meta.tone];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        tone
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      {t(meta.key, meta.fallback)}
    </span>
  );
}
